// src/pages/collection/CollectionProductManagement.js
// Página para gestionar productos de catálogos/colecciones con CRUD completo

import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Card, Alert, Button, Form, Spinner, Table, Modal, Badge } from 'react-bootstrap';
import NavContainer from '../../components/Nav';
import OffCanvasNav from '../../components/OffCanvasNav';
import { useAuth } from '../../context/AuthContext';
import { getCollections } from '../../services/collectionApi';
import { getCollectionProducts as getProductsFromCollection } from '../../services/collectionApi';
import { 
  getCollectionProducts, 
  createCollectionProduct, 
  updateCollectionProduct, 
  deleteCollectionProduct,
  getCollectionProductById,
  addProductImage,
  deleteProductImage
} from '../../services/collectionProductApi';
import { storage } from '../../firebase/firebaseConfig';
import { getDownloadURL, ref, uploadBytesResumable } from 'firebase/storage';
import { Helmet } from 'react-helmet';
import IconUpdate from '../../elements/IconUpdate';
import IconDelete from '../../elements/IconDelete';

const CollectionProductManagement = () => {
  const { user } = useAuth();
  
  // Estados para el listado
  const [collections, setCollections] = useState([]);
  const [selectedCollectionId, setSelectedCollectionId] = useState('');
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  
  // Estados para el modal
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  
  // Estados para el formulario
  const [formData, setFormData] = useState({
    name: '',
    price: '',
    category: '',
    description: '',
    sizes: [],
    details: [],
    order_index: 0,
    images: []
  });
  
  // Estados para tamaños y detalles
  const [sizeInput, setSizeInput] = useState('');
  const [detailInput, setDetailInput] = useState('');
  
  // Estados para carga de imágenes
  const [productImages, setProductImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [uploadingImage, setUploadingImage] = useState(false);

  // Cargar colecciones al montar
  useEffect(() => {
    loadCollections();
  }, []);

  // Cargar productos cuando cambie la colección seleccionada
  useEffect(() => {
    if (selectedCollectionId) {
      loadProducts();
    } else {
      setProducts([]);
    }
  }, [selectedCollectionId]);

  // Cargar colecciones
  const loadCollections = async () => {
    try {
      const { ok, status, data } = await getCollections();
      
      if (ok && (status === 200 || data?.code === 200)) {
        const responseData = data.data || data.message || [];
        setCollections(Array.isArray(responseData) ? responseData : []);
      } else {
        setErrorMsg('Error cargando colecciones');
      }
    } catch (error) {
      console.error('Error cargando colecciones:', error);
      setErrorMsg('Error inesperado al cargar colecciones');
    }
  };

  // Cargar productos de la colección
  const loadProducts = async () => {
    try {
      setLoading(true);
      setErrorMsg('');
      
      console.log('Cargando productos para colección:', selectedCollectionId);
      
      // Intentar primero con collectionProduct.php
      let { ok, status, data } = await getCollectionProducts(selectedCollectionId);
      
      // Si falla o no hay datos, intentar con collection.php/{id}/products
      if (!ok || (!data?.data && !data?.message)) {
        console.log('Intentando con collection.php endpoint...');
        const result = await getProductsFromCollection(selectedCollectionId);
        ok = result.ok;
        status = result.status;
        data = result.data;
      }
      
      console.log('Respuesta de productos:', { ok, status, data });
      
      if (ok && (status === 200 || data?.code === 200)) {
        // Intentar diferentes estructuras de respuesta
        let responseData = null;
        
        if (data?.data) {
          // Estructura: { code: 200, data: [...] }
          responseData = data.data;
        } else if (data?.message) {
          // Estructura: { code: 200, message: [...] }
          responseData = data.message;
        } else if (Array.isArray(data)) {
          // Estructura: [...] (directamente un array)
          responseData = data;
        } else if (data?.products) {
          // Estructura: { products: [...] }
          responseData = data.products;
        }
        
        console.log('Datos de productos extraídos:', responseData);
        
        if (Array.isArray(responseData)) {
          setProducts(responseData);
          console.log('Productos cargados:', responseData.length);
        } else if (responseData && typeof responseData === 'object') {
          // Si es un objeto, intentar extraer el array de productos
          const productsArray = responseData.products || Object.values(responseData).find(v => Array.isArray(v));
          setProducts(Array.isArray(productsArray) ? productsArray : []);
          console.log('Productos cargados desde objeto:', productsArray?.length || 0);
        } else {
          console.warn('Formato de respuesta no reconocido:', responseData);
          setProducts([]);
        }
      } else {
        const msg = data?.message || 'Error cargando productos';
        setErrorMsg(`${msg} (status: ${status})`);
        setProducts([]);
      }
    } catch (error) {
      console.error('Error cargando productos:', error);
      setErrorMsg('Error inesperado al cargar productos');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  // Manejar cambios en el formulario
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // Agregar tamaño
  const handleAddSize = () => {
    if (sizeInput.trim() && !formData.sizes.includes(sizeInput.trim())) {
      setFormData(prev => ({
        ...prev,
        sizes: [...prev.sizes, sizeInput.trim()]
      }));
      setSizeInput('');
    }
  };

  // Eliminar tamaño
  const handleRemoveSize = (size) => {
    setFormData(prev => ({
      ...prev,
      sizes: prev.sizes.filter(s => s !== size)
    }));
  };

  // Agregar detalle
  const handleAddDetail = () => {
    if (detailInput.trim() && !formData.details.includes(detailInput.trim())) {
      setFormData(prev => ({
        ...prev,
        details: [...prev.details, detailInput.trim()]
      }));
      setDetailInput('');
    }
  };

  // Eliminar detalle
  const handleRemoveDetail = (detail) => {
    setFormData(prev => ({
      ...prev,
      details: prev.details.filter(d => d !== detail)
    }));
  };

  // Subir múltiples imágenes a Firebase
  const uploadImagesToFirebase = async (imageFiles) => {
    const uploadPromises = imageFiles.map(async (imageFile) => {
      return new Promise((resolve, reject) => {
        const metadata = {
          contentType: imageFile.type || 'image/jpeg'
        };

        const newFileName = `product_${Date.now()}_${Math.random().toString(36).substring(7)}.${imageFile.name.split('.').pop()}`;
        const storageRef = ref(storage, `collections/products/${newFileName}`);
        const uploadTask = uploadBytesResumable(storageRef, imageFile, metadata);

        uploadTask.on('state_changed',
          (snapshot) => {
            const progress = (snapshot.bytesTransferred / snapshot.totalBytes) * 100;
            console.log('Upload is ' + progress + '% done');
          },
          (error) => {
            console.error('Error al subir imagen:', error);
            reject(error);
          },
          () => {
            getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
              resolve(downloadURL);
            }).catch(reject);
          }
        );
      });
    });

    return Promise.all(uploadPromises);
  };

  // Manejar selección de imágenes
  const handleImageChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const files = Array.from(e.target.files);
      setProductImages(prev => [...prev, ...files]);
      
      // Crear previews
      files.forEach(file => {
        const reader = new FileReader();
        reader.onloadend = () => {
          setImagePreviews(prev => [...prev, reader.result]);
        };
        reader.readAsDataURL(file);
      });
    }
  };

  // Eliminar imagen de la lista
  const handleRemoveImage = (index) => {
    setProductImages(prev => prev.filter((_, i) => i !== index));
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    
    // Si hay imágenes ya guardadas, también eliminarlas
    if (formData.images[index] && typeof formData.images[index] === 'string') {
      setFormData(prev => ({
        ...prev,
        images: prev.images.filter((_, i) => i !== index)
      }));
    }
  };

  // Abrir modal de creación
  const handleOpenCreateModal = () => {
    if (!selectedCollectionId) {
      setErrorMsg('Por favor selecciona una colección primero');
      return;
    }
    
    setFormData({
      name: '',
      price: '',
      category: '',
      description: '',
      sizes: [],
      details: [],
      order_index: 0,
      images: []
    });
    setProductImages([]);
    setImagePreviews([]);
    setSizeInput('');
    setDetailInput('');
    setShowCreateModal(true);
  };

  // Cerrar modal de creación
  const handleCloseCreateModal = () => {
    setShowCreateModal(false);
    setFormData({
      name: '',
      price: '',
      category: '',
      description: '',
      sizes: [],
      details: [],
      order_index: 0,
      images: []
    });
    setProductImages([]);
    setImagePreviews([]);
    setSizeInput('');
    setDetailInput('');
  };

  // Abrir modal de edición
  const handleOpenEditModal = async (product) => {
    try {
      setModalLoading(true);
      console.log('Abriendo modal de edición para producto:', product);
      
      // Usar los datos del producto directamente si están disponibles
      let productData = { ...product };
      
      // Intentar cargar datos completos desde la API
      try {
        console.log('Cargando datos completos del producto desde API...');
        const { ok, data } = await getCollectionProductById(selectedCollectionId, product.id);
        
        console.log('Respuesta de getCollectionProductById:', { ok, data });
        
        if (ok) {
          // Intentar diferentes estructuras de respuesta
          let apiProductData = null;
          
          if (data?.code === 200 || data?.status === 'ok') {
            // Estructura: { code: 200, data: {...} }
            apiProductData = data.data || data.message || data;
          } else if (data?.data) {
            // Estructura: { data: {...} }
            apiProductData = data.data;
          } else if (data?.message) {
            // Estructura: { message: {...} }
            apiProductData = data.message;
          } else if (data && typeof data === 'object') {
            // Estructura directa: {...}
            apiProductData = data;
          }
          
          if (apiProductData) {
            // Combinar datos de la API con los del producto (API tiene prioridad)
            productData = { ...productData, ...apiProductData };
            console.log('Datos combinados del producto:', productData);
          }
        }
      } catch (apiError) {
        console.warn('No se pudieron cargar datos adicionales desde la API, usando datos del producto:', apiError);
        // Continuar con los datos del producto que ya tenemos
      }
      
      console.log('Datos finales del producto a usar:', productData);
      
      // Convertir imágenes a formato correcto
      let images = [];
      let previews = [];
      
      // Manejar image_url (string único) - formato retornado por la API
      if (productData.image_url) {
        const imageUrl = typeof productData.image_url === 'string' 
          ? productData.image_url 
          : productData.image_url;
        // Limpiar barras invertidas escapadas si existen
        const cleanUrl = imageUrl.replace(/\\\//g, '/');
        images.push(cleanUrl);
        previews.push(cleanUrl);
        console.log('Imagen encontrada en image_url:', cleanUrl);
      }
      
      // Manejar images (array) - formato alternativo
      if (productData.images && Array.isArray(productData.images)) {
        productData.images.forEach((img, index) => {
          if (typeof img === 'string') {
            // Limpiar barras invertidas escapadas si existen
            const cleanUrl = img.replace(/\\\//g, '/');
            if (!images.includes(cleanUrl)) {
              images.push(cleanUrl);
              previews.push(cleanUrl);
            }
          } else if (img && img.url) {
            const cleanUrl = img.url.replace(/\\\//g, '/');
            if (!images.includes(cleanUrl)) {
              images.push(cleanUrl);
              previews.push(cleanUrl);
            }
          } else if (img && img.image_url) {
            // Manejar caso donde puede venir como objeto con image_url
            const cleanUrl = img.image_url.replace(/\\\//g, '/');
            if (!images.includes(cleanUrl)) {
              images.push(cleanUrl);
              previews.push(cleanUrl);
            }
          }
        });
        console.log('Imágenes encontradas en images array:', images.length);
      }
      
      // Si no hay imágenes en ninguno de los formatos anteriores, buscar en otras propiedades
      if (images.length === 0) {
        // Buscar cualquier propiedad que contenga 'image' o 'url'
        Object.keys(productData).forEach(key => {
          if (key.toLowerCase().includes('image') || key.toLowerCase().includes('url')) {
            const value = productData[key];
            if (typeof value === 'string' && value.startsWith('http')) {
              const cleanUrl = value.replace(/\\\//g, '/');
              images.push(cleanUrl);
              previews.push(cleanUrl);
              console.log(`Imagen encontrada en ${key}:`, cleanUrl);
            }
          }
        });
      }
      
      console.log('Total de imágenes procesadas:', images.length, images);
      
      // Asegurar que sizes y details sean arrays
      let sizes = [];
      if (productData.sizes) {
        if (Array.isArray(productData.sizes)) {
          sizes = productData.sizes;
        } else if (typeof productData.sizes === 'string') {
          // Si es un string, intentar parsearlo si es JSON
          try {
            sizes = JSON.parse(productData.sizes);
          } catch (e) {
            // Si no es JSON, tratarlo como string simple
            sizes = productData.sizes.split(',').map(s => s.trim()).filter(s => s);
          }
        }
      }
      
      let details = [];
      if (productData.details) {
        if (Array.isArray(productData.details)) {
          details = productData.details;
        } else if (typeof productData.details === 'string') {
          // Si es un string, intentar parsearlo si es JSON
          try {
            details = JSON.parse(productData.details);
          } catch (e) {
            // Si no es JSON, tratarlo como string simple
            details = [productData.details];
          }
        }
      }
      
      const formDataToSet = {
        name: productData.name || '',
        price: productData.price?.toString() || '',
        category: productData.category || '',
        description: productData.description || '',
        sizes: sizes,
        details: details,
        order_index: productData.order_index || 0,
        images: images
      };
      
      setFormData(formDataToSet);
      setImagePreviews(previews);
      setSelectedProduct(product);
      setShowEditModal(true);
      
      console.log('FormData actualizado:', formDataToSet);
    } catch (error) {
      console.error('Error al cargar datos del producto:', error);
      setErrorMsg('Error al cargar datos del producto: ' + (error.message || 'Error desconocido'));
    } finally {
      setModalLoading(false);
    }
  };

  // Cerrar modal de edición
  const handleCloseEditModal = () => {
    setShowEditModal(false);
    setSelectedProduct(null);
    setFormData({
      name: '',
      price: '',
      category: '',
      description: '',
      sizes: [],
      details: [],
      order_index: 0,
      images: []
    });
    setProductImages([]);
    setImagePreviews([]);
    setSizeInput('');
    setDetailInput('');
  };

  // Abrir modal de eliminación
  const handleOpenDeleteModal = (product) => {
    setSelectedProduct(product);
    setShowDeleteModal(true);
  };

  // Cerrar modal de eliminación
  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setSelectedProduct(null);
  };

  // Manejar creación de producto
  const handleCreateProduct = async (e) => {
    e.preventDefault();
    try {
      setModalLoading(true);
      setUploadingImage(true);
      
      let imageUrls = [];
      
      // Subir nuevas imágenes a Firebase si hay
      if (productImages.length > 0) {
        imageUrls = await uploadImagesToFirebase(productImages);
      }
      
      // Combinar con imágenes existentes si hay
      const allImages = [...formData.images, ...imageUrls];
      
      const productData = {
        name: formData.name,
        price: parseFloat(formData.price),
        category: formData.category,
        description: formData.description,
        sizes: formData.sizes,
        details: formData.details,
        order_index: parseInt(formData.order_index) || 0,
        images: allImages // Enviar como array de strings
      };
      
      const { ok, status, data } = await createCollectionProduct(selectedCollectionId, productData);
      
      if (ok && (status === 201 || data?.code === 201)) {
        setSuccessMsg(data?.message || 'Producto creado exitosamente');
        setShowCreateModal(false);
        loadProducts();
      } else {
        const msg = data?.message || 'Error creando producto';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error creando producto:', error);
      setErrorMsg('Error inesperado al crear producto');
    } finally {
      setModalLoading(false);
      setUploadingImage(false);
    }
  };

  // Manejar actualización de producto
  const handleUpdateProduct = async (e) => {
    e.preventDefault();
    try {
      setModalLoading(true);
      setUploadingImage(true);
      
      let imageUrls = [];
      
      // Subir nuevas imágenes a Firebase si hay
      if (productImages.length > 0) {
        imageUrls = await uploadImagesToFirebase(productImages);
      }
      
      // Combinar con imágenes existentes
      const allImages = [...formData.images, ...imageUrls];
      
      const productData = {
        name: formData.name,
        price: parseFloat(formData.price),
        category: formData.category,
        description: formData.description,
        sizes: formData.sizes,
        details: formData.details,
        order_index: parseInt(formData.order_index) || 0,
        images: allImages
      };
      
      const { ok, status, data } = await updateCollectionProduct(
        selectedCollectionId, 
        selectedProduct.id, 
        productData
      );
      
      if (ok && (status === 200 || data?.code === 200)) {
        setSuccessMsg(data?.message || 'Producto actualizado exitosamente');
        setShowEditModal(false);
        setSelectedProduct(null);
        loadProducts();
      } else {
        const msg = data?.message || 'Error actualizando producto';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error actualizando producto:', error);
      setErrorMsg('Error inesperado al actualizar producto');
    } finally {
      setModalLoading(false);
      setUploadingImage(false);
    }
  };

  // Manejar eliminación de producto
  const handleDeleteProduct = async () => {
    try {
      setModalLoading(true);
      
      const { ok, status, data } = await deleteCollectionProduct(selectedCollectionId, selectedProduct.id);
      
      if (ok && (status === 200 || data?.code === 200)) {
        setSuccessMsg(data?.message || 'Producto eliminado exitosamente');
        setShowDeleteModal(false);
        setSelectedProduct(null);
        loadProducts();
      } else {
        const msg = data?.message || 'Error eliminando producto';
        setErrorMsg(`${msg} (status: ${status})`);
      }
    } catch (error) {
      console.error('Error eliminando producto:', error);
      setErrorMsg('Error inesperado al eliminar producto');
    } finally {
      setModalLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Gestión de Productos de Catálogos - DotoniWoof</title>
      </Helmet>
      <NavContainer />
      <OffCanvasNav />

      <Container fluid className="mt-3">
        <Row>
          <Col>
            <Card>
              <Card.Header className="d-flex justify-content-between align-items-center">
                <h4 className="mb-0">Gestión de Productos de Catálogos</h4>
                <Button 
                  variant="primary" 
                  onClick={handleOpenCreateModal}
                  disabled={!selectedCollectionId}
                >
                  <i className="fas fa-plus me-2"></i>
                  Nuevo Producto
                </Button>
              </Card.Header>
              <Card.Body>
                {errorMsg && (
                  <Alert variant="danger" onClose={() => setErrorMsg('')} dismissible>
                    {errorMsg}
                  </Alert>
                )}
                {successMsg && (
                  <Alert variant="success" onClose={() => setSuccessMsg('')} dismissible>
                    {successMsg}
                  </Alert>
                )}

                {/* Selector de colección */}
                <Row className="mb-3">
                  <Col md={6}>
                    <Form.Group>
                      <Form.Label><strong>Seleccionar Catálogo</strong></Form.Label>
                      <Form.Select
                        value={selectedCollectionId}
                        onChange={(e) => setSelectedCollectionId(e.target.value)}
                      >
                        <option value="">-- Selecciona un catálogo --</option>
                        {collections.map((collection) => (
                          <option key={collection.id} value={collection.id}>
                            {collection.title || `Catálogo #${collection.id}`}
                          </option>
                        ))}
                      </Form.Select>
                    </Form.Group>
                  </Col>
                </Row>

                {/* Tabla de productos */}
                {loading ? (
                  <div className="d-flex justify-content-center py-4">
                    <Spinner animation="border" />
                  </div>
                ) : (
                  <>
                    {selectedCollectionId ? (
                      <Table striped bordered hover responsive>
                        <thead>
                          <tr>
                            <th>ID</th>
                            <th>Nombre</th>
                            <th>Precio</th>
                            <th>Categoría</th>
                            <th>Tamaños</th>
                            <th>Imágenes</th>
                            <th>Acciones</th>
                          </tr>
                        </thead>
                        <tbody>
                          {products.length === 0 ? (
                            <tr>
                              <td colSpan="7" className="text-center py-4">
                                No hay productos en este catálogo
                              </td>
                            </tr>
                          ) : (
                            products.map((product) => (
                              <tr key={product.id}>
                                <td>{product.id}</td>
                                <td>{product.name || 'N/A'}</td>
                                <td>${product.price || '0.00'}</td>
                                <td>{product.category || 'N/A'}</td>
                                <td>
                                  {product.sizes && product.sizes.length > 0 ? (
                                    <div>
                                      {product.sizes.map((size, idx) => (
                                        <Badge key={idx} bg="secondary" className="me-1">
                                          {size}
                                        </Badge>
                                      ))}
                                    </div>
                                  ) : (
                                    'N/A'
                                  )}
                                </td>
                                <td>
                                  {(() => {
                                    // Obtener imágenes del producto (puede venir como image_url o images)
                                    let productImages = [];
                                    
                                    // Si tiene image_url (string único)
                                    if (product.image_url) {
                                      const cleanUrl = typeof product.image_url === 'string' 
                                        ? product.image_url.replace(/\\\//g, '/')
                                        : product.image_url;
                                      productImages.push(cleanUrl);
                                    }
                                    
                                    // Si tiene images (array)
                                    if (product.images && Array.isArray(product.images) && product.images.length > 0) {
                                      product.images.forEach(img => {
                                        if (typeof img === 'string') {
                                          const cleanUrl = img.replace(/\\\//g, '/');
                                          if (!productImages.includes(cleanUrl)) {
                                            productImages.push(cleanUrl);
                                          }
                                        } else if (img && img.url) {
                                          const cleanUrl = img.url.replace(/\\\//g, '/');
                                          if (!productImages.includes(cleanUrl)) {
                                            productImages.push(cleanUrl);
                                          }
                                        } else if (img && img.image_url) {
                                          const cleanUrl = img.image_url.replace(/\\\//g, '/');
                                          if (!productImages.includes(cleanUrl)) {
                                            productImages.push(cleanUrl);
                                          }
                                        }
                                      });
                                    }
                                    
                                    if (productImages.length > 0) {
                                      return (
                                        <div className="d-flex gap-1">
                                          {productImages.slice(0, 3).map((imgUrl, idx) => (
                                            <img
                                              key={idx}
                                              src={imgUrl}
                                              alt={`Product ${idx + 1}`}
                                              style={{ width: '40px', height: '40px', objectFit: 'cover', borderRadius: '4px' }}
                                              onError={(e) => {
                                                console.error('Error cargando imagen:', imgUrl);
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          ))}
                                          {productImages.length > 3 && (
                                            <Badge bg="info">+{productImages.length - 3}</Badge>
                                          )}
                                        </div>
                                      );
                                    } else {
                                      return 'Sin imágenes';
                                    }
                                  })()}
                                </td>
                                <td>
                                  <Button 
                                    variant="outline-primary" 
                                    size="sm" 
                                    className="me-2"
                                    onClick={() => handleOpenEditModal(product)}
                                  >
                                    <IconUpdate />
                                    Editar
                                  </Button>
                                  <Button 
                                    variant="outline-danger" 
                                    size="sm"
                                    onClick={() => handleOpenDeleteModal(product)}
                                  >
                                    <IconDelete />
                                    Eliminar
                                  </Button>
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </Table>
                    ) : (
                      <Alert variant="info">
                        Por favor selecciona un catálogo para ver sus productos
                      </Alert>
                    )}
                  </>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Modal de creación de producto */}
        <Modal show={showCreateModal} onHide={handleCloseCreateModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Crear Nuevo Producto</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form onSubmit={handleCreateProduct}>
              <Row>
                <Col md={6}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Nombre del Producto *</strong></Form.Label>
                    <Form.Control
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Ej: GABARDINA TÉCNICA BEIGE"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Precio *</strong></Form.Label>
                    <Form.Control
                      type="number"
                      step="0.01"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      placeholder="89.95"
                      required
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group className="mb-3">
                    <Form.Label><strong>Índice de Orden</strong></Form.Label>
                    <Form.Control
                      type="number"
                      name="order_index"
                      value={formData.order_index}
                      onChange={handleInputChange}
                      placeholder="0"
                    />
                  </Form.Group>
                </Col>
              </Row>

              <Form.Group className="mb-3">
                <Form.Label><strong>Categoría *</strong></Form.Label>
                <Form.Control
                  type="text"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                  placeholder="Ej: Abrigos"
                  required
                />
              </Form.Group>

              <Form.Group className="mb-3">
                <Form.Label><strong>Descripción</strong></Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  name="description"
                  value={formData.description}
                  onChange={handleInputChange}
                  placeholder="Descripción del producto"
                />
              </Form.Group>

              {/* Tamaños */}
              <Form.Group className="mb-3">
                <Form.Label><strong>Tamaños</strong></Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="text"
                    value={sizeInput}
                    onChange={(e) => setSizeInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddSize();
                      }
                    }}
                    placeholder="Ej: XS, S, M, L"
                  />
                  <Button variant="outline-secondary" onClick={handleAddSize} type="button">
                    Agregar
                  </Button>
                </div>
                {formData.sizes.length > 0 && (
                  <div className="d-flex flex-wrap gap-2">
                    {formData.sizes.map((size, idx) => (
                      <Badge key={idx} bg="primary" style={{ fontSize: '0.9rem', padding: '0.5rem' }}>
                        {size}
                        <Button
                          variant="link"
                          className="text-white p-0 ms-2"
                          onClick={() => handleRemoveSize(size)}
                          style={{ textDecoration: 'none' }}
                        >
                          ×
                        </Button>
                      </Badge>
                    ))}
                  </div>
                )}
              </Form.Group>

              {/* Detalles */}
              <Form.Group className="mb-3">
                <Form.Label><strong>Detalles</strong></Form.Label>
                <div className="d-flex gap-2 mb-2">
                  <Form.Control
                    type="text"
                    value={detailInput}
                    onChange={(e) => setDetailInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleAddDetail();
                      }
                    }}
                    placeholder="Ej: Exterior: 100% Algodón"
                  />
                  <Button variant="outline-secondary" onClick={handleAddDetail} type="button">
                    Agregar
                  </Button>
                </div>
                {formData.details.length > 0 && (
                  <ul className="list-unstyled">
                    {formData.details.map((detail, idx) => (
                      <li key={idx} className="d-flex justify-content-between align-items-center mb-2">
                        <span>{detail}</span>
                        <Button
                          variant="link"
                          className="text-danger p-0"
                          onClick={() => handleRemoveDetail(detail)}
                        >
                          Eliminar
                        </Button>
                      </li>
                    ))}
                  </ul>
                )}
              </Form.Group>

              {/* Imágenes */}
              <Form.Group className="mb-3">
                <Form.Label><strong>Imágenes</strong></Form.Label>
                <Form.Control
                  type="file"
                  accept="image/*"
                  multiple
                  onChange={handleImageChange}
                />
                {imagePreviews.length > 0 && (
                  <div className="d-flex flex-wrap gap-2 mt-3">
                    {imagePreviews.map((preview, idx) => {
                      // Asegurar que la URL esté limpia (sin barras invertidas escapadas)
                      const cleanUrl = typeof preview === 'string' 
                        ? preview.replace(/\\\//g, '/')
                        : preview;
                      
                      return (
                        <div key={idx} className="position-relative" style={{ width: '120px', height: '120px' }}>
                          <img
                            src={cleanUrl}
                            alt={`Preview ${idx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                            onError={(e) => {
                              console.error('Error cargando imagen en preview:', cleanUrl);
                              e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="120" height="120"%3E%3Crect width="120" height="120" fill="%23ddd"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EError%3C/text%3E%3C/svg%3E';
                            }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0"
                            onClick={() => handleRemoveImage(idx)}
                            style={{ borderRadius: '50%', width: '24px', height: '24px', padding: 0 }}
                          >
                            ×
                          </Button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </Form.Group>

              <div className="d-flex justify-content-end gap-2">
                <Button variant="secondary" onClick={handleCloseCreateModal}>
                  Cancelar
                </Button>
                <Button variant="primary" type="submit" disabled={modalLoading || uploadingImage}>
                  {modalLoading || uploadingImage ? (
                    <>
                      <Spinner animation="border" size="sm" className="me-2" />
                      {uploadingImage ? 'Subiendo imágenes...' : 'Creando...'}
                    </>
                  ) : (
                    'Crear Producto'
                  )}
                </Button>
              </div>
            </Form>
          </Modal.Body>
        </Modal>

        {/* Modal de edición de producto */}
        <Modal show={showEditModal} onHide={handleCloseEditModal} size="xl">
          <Modal.Header closeButton>
            <Modal.Title>Editar Producto</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {modalLoading && !selectedProduct ? (
              <div className="d-flex justify-content-center py-4">
                <Spinner animation="border" />
              </div>
            ) : (
              <Form onSubmit={handleUpdateProduct}>
                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Nombre del Producto *</strong></Form.Label>
                      <Form.Control
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Precio *</strong></Form.Label>
                      <Form.Control
                        type="number"
                        step="0.01"
                        name="price"
                        value={formData.price}
                        onChange={handleInputChange}
                        required
                      />
                    </Form.Group>
                  </Col>
                  <Col md={3}>
                    <Form.Group className="mb-3">
                      <Form.Label><strong>Índice de Orden</strong></Form.Label>
                      <Form.Control
                        type="number"
                        name="order_index"
                        value={formData.order_index}
                        onChange={handleInputChange}
                      />
                    </Form.Group>
                  </Col>
                </Row>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Categoría *</strong></Form.Label>
                  <Form.Control
                    type="text"
                    name="category"
                    value={formData.category}
                    onChange={handleInputChange}
                    required
                  />
                </Form.Group>

                <Form.Group className="mb-3">
                  <Form.Label><strong>Descripción</strong></Form.Label>
                  <Form.Control
                    as="textarea"
                    rows={3}
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                  />
                </Form.Group>

                {/* Tamaños */}
                <Form.Group className="mb-3">
                  <Form.Label><strong>Tamaños</strong></Form.Label>
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="text"
                      value={sizeInput}
                      onChange={(e) => setSizeInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddSize();
                        }
                      }}
                      placeholder="Ej: XS, S, M, L"
                    />
                    <Button variant="outline-secondary" onClick={handleAddSize} type="button">
                      Agregar
                    </Button>
                  </div>
                  {formData.sizes.length > 0 && (
                    <div className="d-flex flex-wrap gap-2">
                      {formData.sizes.map((size, idx) => (
                        <Badge key={idx} bg="primary" style={{ fontSize: '0.9rem', padding: '0.5rem' }}>
                          {size}
                          <Button
                            variant="link"
                            className="text-white p-0 ms-2"
                            onClick={() => handleRemoveSize(size)}
                            style={{ textDecoration: 'none' }}
                          >
                            ×
                          </Button>
                        </Badge>
                      ))}
                    </div>
                  )}
                </Form.Group>

                {/* Detalles */}
                <Form.Group className="mb-3">
                  <Form.Label><strong>Detalles</strong></Form.Label>
                  <div className="d-flex gap-2 mb-2">
                    <Form.Control
                      type="text"
                      value={detailInput}
                      onChange={(e) => setDetailInput(e.target.value)}
                      onKeyPress={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                          handleAddDetail();
                        }
                      }}
                      placeholder="Ej: Exterior: 100% Algodón"
                    />
                    <Button variant="outline-secondary" onClick={handleAddDetail} type="button">
                      Agregar
                    </Button>
                  </div>
                  {formData.details.length > 0 && (
                    <ul className="list-unstyled">
                      {formData.details.map((detail, idx) => (
                        <li key={idx} className="d-flex justify-content-between align-items-center mb-2">
                          <span>{detail}</span>
                          <Button
                            variant="link"
                            className="text-danger p-0"
                            onClick={() => handleRemoveDetail(detail)}
                          >
                            Eliminar
                          </Button>
                        </li>
                      ))}
                    </ul>
                  )}
                </Form.Group>

                {/* Imágenes existentes y nuevas */}
                <Form.Group className="mb-3">
                  <Form.Label><strong>Imágenes</strong></Form.Label>
                  <Form.Control
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageChange}
                  />
                  {imagePreviews.length > 0 && (
                    <div className="d-flex flex-wrap gap-2 mt-3">
                      {imagePreviews.map((preview, idx) => (
                        <div key={idx} className="position-relative" style={{ width: '120px', height: '120px' }}>
                          <img
                            src={preview}
                            alt={`Preview ${idx + 1}`}
                            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '4px' }}
                          />
                          <Button
                            variant="danger"
                            size="sm"
                            className="position-absolute top-0 end-0"
                            onClick={() => handleRemoveImage(idx)}
                            style={{ borderRadius: '50%', width: '24px', height: '24px', padding: 0 }}
                          >
                            ×
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </Form.Group>

                <div className="d-flex justify-content-end gap-2">
                  <Button variant="secondary" onClick={handleCloseEditModal}>
                    Cancelar
                  </Button>
                  <Button variant="primary" type="submit" disabled={modalLoading || uploadingImage}>
                    {modalLoading || uploadingImage ? (
                      <>
                        <Spinner animation="border" size="sm" className="me-2" />
                        {uploadingImage ? 'Subiendo imágenes...' : 'Actualizando...'}
                      </>
                    ) : (
                      'Actualizar Producto'
                    )}
                  </Button>
                </div>
              </Form>
            )}
          </Modal.Body>
        </Modal>

        {/* Modal de confirmación de eliminación */}
        <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
          <Modal.Header closeButton>
            <Modal.Title>Confirmar Eliminación</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            ¿Estás seguro de que deseas eliminar el producto "{selectedProduct?.name}"? 
            Esta acción no se puede deshacer.
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleCloseDeleteModal}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleDeleteProduct} disabled={modalLoading}>
              {modalLoading ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Eliminando...
                </>
              ) : (
                'Eliminar'
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </Container>
    </>
  );
};

export default CollectionProductManagement;
